import React, { CSSProperties, forwardRef, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';

import { bemCssModules, getTestId } from '@utils';
import { useIsMobile } from '@utils/hooks';

import CloseIcon from './icon_close.svg';

import style from './NewModal.scss';

const cn = bemCssModules(style, 'NewModal');
const ti = getTestId('Modal');

const ANIMATION_DURATION = 200;

export type TModal = {
  className?: string;
  children: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  hideCloseButton?: boolean;
  hideMobileCloseButton?: boolean;
  overlay?: boolean;
  disableScroll?: boolean;
  slide?: boolean;
  fade?: boolean;
  notPortal?: boolean;
};

const Modal = forwardRef<HTMLDivElement, TModal>((props, ref) => {
  const {
    className,
    isOpen,
    onClose,
    children,
    hideCloseButton,
    hideMobileCloseButton,
    overlay = false,
    disableScroll = false,
    slide = true,
    fade = false,
    notPortal = false,
  } = props;

  const [isClosed, setIsClosed] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isOpen) setIsClosed(false);
    else setTimeout(() => setIsClosed(true), ANIMATION_DURATION);
  }, [isOpen]);

  const handleClose = () => onClose?.();

  const styles = {
    '--fade-duration': fade ? `${ANIMATION_DURATION}ms` : 0,
    '--slide-duration': slide ? `${ANIMATION_DURATION}ms` : 0,
  } as CSSProperties;

  if (isClosed) return null;

  const modal = (
    <div
      className={cn(
        { hidden: fade ? isClosed : !isOpen },
        isOpen && (overlay || disableScroll) && ['disable-scroll']
      )}
      style={styles}
      data-testid={ti('modal')}
    >
      {overlay && (
        <div
          className={cn('overlay')}
          onClick={onClose}
          role="button"
          tabIndex={-1}
          onKeyDown={onClose}
          aria-label="overlay"
          data-testid={ti('overlay')}
        />
      )}
      <div className={cn('modal', { hidden: !isOpen }, [className])} ref={ref}>
        <div className={cn('close')}>
          <button
            type="button"
            data-testid={ti('button')}
            className={cn('button', {
              hidden: hideCloseButton,
              hiddenMobile: hideMobileCloseButton,
            })}
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  return isMobile || !notPortal ? createPortal(modal, document.body) : modal;
});

export default Modal;
